import { useState, type MouseEvent } from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useNavigate } from 'react-router-dom';
import {
  AgronodCard,
  AgronodCheckbox,
  AgronodIcon,
  AgronodTypography,
  Tooltip,
} from '@agronod/mui-components';
import { RouterLink } from '../../components/RouterLink';
import {
  avvikelseText,
  gardTitel,
  GardSektion,
  modellRad,
  RevisionGard,
  RevisionKolumn,
} from '../../utils/api';
import { MattRubrik } from './MattRubrik';
import { ForetagBorttaget } from './ForetagBorttaget';
import { StatusCell } from './StatusCell';
import { rubrikSx } from './tabellStil';

interface Urval {
  valda: Set<number>;
  /** Låst antal – fler går inte att kryssa i. */
  max: number;
  /** Kryssa i eller ur flera gårdar på en gång (shift-klick väljer intervall). */
  valj: (nummer: number[], valj: boolean) => void;
}

interface Props {
  sektion: GardSektion;
  /** Måttkolumnerna i modellens ordning; tom för "Utan bedömning". */
  kolumner: RevisionKolumn[];
  detaljUrl: (nummer: number) => string;
  /** Sätts när revisionsledaren får kryssa i gårdar (antal låst, urval öppet). */
  urval?: Urval;
}

/** Gårdens namn/pseudonym med organisationsnummer som underrad. */
export const GardCell = ({ gard }: { gard: RevisionGard }) => (
  <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>
    <Box sx={{ fontWeight: 500, color: gard.identitet ? 'text.primary' : 'text.secondary' }}>
      {gardTitel(gard)}
      {gard.identitet && (
        <AgronodTypography
          component="span"
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 400, ml: 1 }}
        >
          Gård {gard.nummer}
        </AgronodTypography>
      )}
    </Box>
    <AgronodTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
      {gard.identitet
        ? (gard.identitet.orgnr ??
          (gard.identitet.foretagBorttaget ? <ForetagBorttaget /> : 'Org.nr saknas'))
        : 'Namn och org.nr dolt'}
    </AgronodTypography>
  </Box>
);

/**
 * Tabell för en modells lista (kravspec v4 5.3): en rad per gård med
 * pseudonym/identitet, avvikelsemåttet och en kolumn per nyckeltal/
 * kontrollpunkt med läget mot förväntat intervall. Inga värden visas.
 * Raderna ligger i rangordning (störst avvikelse först) men rangtalet visas
 * inte – det förväxlades med gårdens pseudonymnummer.
 *
 * Två lägen: med `urval` kryssas gårdar i genom klick på raden (shift-klick
 * väljer ett intervall, fulla urval stänger av övriga rader); utan `urval`
 * öppnar klick på raden gårdens detaljvy. Samma tabell används av
 * revisionsledarens lista och de avpseudonymiserade.
 */
export const GardTabell = ({ sektion, kolumner, detaljUrl, urval }: Props) => {
  const navigate = useNavigate();
  const [senast, setSenast] = useState<number | null>(null);
  const modell = sektion.modell;
  const kanValja = !!urval;
  const fullt = !!urval && urval.valda.size >= urval.max;

  const toggle = (gard: RevisionGard, shift: boolean) => {
    if (!urval) {
      return;
    }
    const valj = !urval.valda.has(gard.nummer);
    let nummer = [gard.nummer];
    if (shift && senast !== null && senast !== gard.nummer) {
      const ordning = sektion.gardar.map((g) => g.nummer);
      const a = ordning.indexOf(senast);
      const b = ordning.indexOf(gard.nummer);
      if (a >= 0 && b >= 0) {
        nummer = ordning.slice(Math.min(a, b), Math.max(a, b) + 1);
      }
    }
    urval.valj(nummer, valj);
    setSenast(gard.nummer);
  };

  const rubrik = (text: string, sx = {}) => (
    <TableCell sx={{ ...rubrikSx, ...sx }}>{text}</TableCell>
  );

  return (
    <AgronodCard sx={{ p: 0 }}>
      <TableContainer>
        <Table size="small" aria-label={sektion.titel}>
          <TableHead>
            <TableRow>
              {kanValja && <TableCell padding="checkbox" />}
              {rubrik('Gård', { minWidth: 160 })}
              {modell && (
                <TableCell
                  align="right"
                  sx={{ verticalAlign: 'bottom', whiteSpace: 'nowrap' }}
                >
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
                  >
                    <Box component="span" sx={rubrikSx}>
                      Avvikelse
                    </Box>
                    <Tooltip title="Skillnaden mellan gårdens beräknade klimatavtryck och modellens prediktion (kg CO2e/kg). Listan är sorterad med störst avvikelse först.">
                      <Box component="span" sx={{ display: 'inline-flex' }}>
                        <AgronodIcon name="infoOutlined" sx={{ fontSize: 14, color: 'icon.secondary' }} />
                      </Box>
                    </Tooltip>
                  </Stack>
                </TableCell>
              )}
              {kolumner.map((m) => (
                <MattRubrik key={m.falt} kolumn={m} />
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sektion.gardar.map((gard) => {
              const rad = modell ? modellRad(gard, modell) : undefined;
              const statusPerFalt = new Map(
                (rad?.matt ?? []).map((m) => [m.falt, m.status]),
              );
              const vald = urval?.valda.has(gard.nummer) ?? false;
              // Urvalsläge: raden går att kryssa i tills antalet är fullt, en
              // vald rad kan alltid kryssas ur. Annars öppnar raden detaljvyn.
              const avstangd = kanValja && !vald && fullt;
              const onClick = kanValja
                ? (e: MouseEvent) => !avstangd && toggle(gard, e.shiftKey)
                : () => navigate(detaljUrl(gard.nummer));
              return (
                <TableRow
                  key={gard.nummer}
                  onClick={onClick}
                  aria-selected={kanValja ? vald : undefined}
                  sx={{
                    cursor: avstangd ? 'not-allowed' : 'pointer',
                    opacity: avstangd ? 0.45 : 1,
                    bgcolor: vald ? 'primary.hint' : undefined,
                    '&:hover': {
                      bgcolor: avstangd
                        ? undefined
                        : kanValja
                          ? 'primary.pastel'
                          : 'primary.hint',
                    },
                  }}
                >
                  {kanValja && (
                    <TableCell padding="checkbox">
                      <AgronodCheckbox
                        size="small"
                        noPadding
                        checked={vald}
                        disabled={avstangd}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(gard, e.shiftKey);
                        }}
                        onChange={() => undefined}
                        label={
                          <Box component="span" sx={visuallyHidden}>
                            Välj {gardTitel(gard)}
                          </Box>
                        }
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {kanValja ? (
                      <GardCell gard={gard} />
                    ) : (
                      // Länken ger tangentbordsåtkomst; klick på hela raden
                      // navigerar redan, så länkens klick stoppas från att bubbla.
                      <RouterLink
                        to={detaljUrl(gard.nummer)}
                        onClick={(e: MouseEvent) => e.stopPropagation()}
                        sx={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <GardCell gard={gard} />
                      </RouterLink>
                    )}
                  </TableCell>
                  {modell && (
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {rad ? avvikelseText(rad.absDifferens) : ''}
                    </TableCell>
                  )}
                  {kolumner.map((m) => (
                    <TableCell key={m.falt} align="center" sx={{ px: 0.75 }}>
                      <StatusCell status={statusPerFalt.get(m.falt)} />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </AgronodCard>
  );
};
